import React from 'react';
import { Link2, Plus, Pencil, Info, Zap } from 'lucide-react';
import { AutomationRequestModal } from './AutomationRequestModal';

/**
 * Sources de réservation (maquette 16d).
 *
 * Volontairement SANS formulaire de clé API : la connexion à une plateforme viendra plus tard, et
 * afficher un champ qui ne mène nulle part ferait croire qu'il suffit de le remplir. Le panneau dit
 * ce qui existe aujourd'hui — l'import et la saisie à la main cohabitent dans le même tableau — et
 * réserve la place du connecteur.
 */

const DOT_IMPORTED = 'bg-state-info';
const DOT_MANUAL = 'bg-border';

export function ReservationSourcesPanel({ zenchefConnected = false }: { zenchefConnected?: boolean }) {
    // La demande d'automatisation existait déjà (modale, route, table, notification au super admin) mais
    // n'était ouverte par aucun écran : c'est elle qu'on rebranche ici plutôt que d'en écrire une seconde.
    const [askAutomation, setAskAutomation] = React.useState(false);

    return (
        <div className="rounded-2xl bg-surface border border-border overflow-hidden">
            <div className="px-4 py-4 border-b border-border-soft">
                <div className="text-sm font-bold text-content mb-1">Sources de réservation</div>
                <p className="text-[12.5px] leading-relaxed text-content-muted">
                    Les réservations peuvent arriver d'une plateforme ou être saisies à la main. Les deux
                    cohabitent dans le même tableau.
                </p>
            </div>

            <div className="px-4 py-3.5 flex flex-col gap-2">
                <SourceRow
                    icon={<Link2 size={16} />}
                    iconClass="bg-state-info-soft border-state-info/30 text-state-info"
                    name="Zenchef"
                    description="Réservations prépayées, rangées à leur date."
                    badge={zenchefConnected
                        ? <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-state-ok-soft border border-state-ok/30 text-state-ok text-[10.5px] font-bold shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-state-ok" />Connectée
                          </span>
                        : <button type="button" onClick={() => setAskAutomation(true)}
                            className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-surface border border-border text-content text-[10.5px] font-bold shrink-0 hover:border-content transition-colors">
                            <Zap size={11} /> Demander la connexion
                          </button>}
                />

                <SourceRow
                    dashed
                    icon={<Plus size={16} />}
                    iconClass="bg-surface-soft text-content-muted"
                    name="Une autre plateforme"
                    description="TheFork, Resengo… ou la vôtre : transmettez votre clé, nous étudions la connexion."
                    muted
                    badge={<button type="button" onClick={() => setAskAutomation(true)}
                        className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-surface border border-border text-content text-[10.5px] font-bold shrink-0 hover:border-content transition-colors">
                        <Zap size={11} /> Faire la demande
                    </button>}
                />

                <SourceRow
                    icon={<Pencil size={16} />}
                    iconClass="bg-surface-soft border-border text-content"
                    name="Saisie à la main"
                    description="Le téléphone, le comptoir, le passage. Toujours disponible."
                    badge={<span className="inline-flex items-center h-6 px-2.5 rounded-full bg-surface border border-border text-content text-[10.5px] font-bold shrink-0">Toujours active</span>}
                />
            </div>

            <div className="px-4 py-3 bg-surface-soft border-t border-border-soft">
                <div className="flex items-start gap-2.5">
                    <Info size={15} className="text-state-info shrink-0 mt-px" />
                    <span className="text-xs leading-relaxed text-content-muted">
                        Une réservation importée garde son origine : le point bleu en tête de ligne. On peut la
                        compléter à la main — table, encaissement, commentaire — sans qu'elle perde son lien avec
                        la plateforme.
                    </span>
                </div>
            </div>

            {askAutomation && <AutomationRequestModal onClose={() => setAskAutomation(false)} />}
        </div>
    );
}

function SourceRow({ icon, iconClass, name, description, badge, dashed, muted }: {
    icon: React.ReactNode; iconClass: string; name: string; description: string;
    badge: React.ReactNode; dashed?: boolean; muted?: boolean;
}) {
    return (
        <div className={`flex items-center gap-3 flex-wrap px-3 py-3 rounded-[11px] ${
            dashed ? 'bg-surface border border-dashed border-border' : 'bg-surface-soft border border-border'
        }`}>
            <span className={`w-[34px] h-[34px] shrink-0 rounded-[9px] border border-transparent flex items-center justify-center ${iconClass}`}>
                {icon}
            </span>
            <span className="flex-1 min-w-[120px]">
                <span className={`block text-[13px] font-bold ${muted ? 'text-content-muted' : 'text-content'}`}>{name}</span>
                <span className="block text-[11.5px] text-content-muted">{description}</span>
            </span>
            {badge}
        </div>
    );
}

/**
 * Le point d'origine en tête de ligne, partagé par la grille du tableau et sa légende — une seule
 * définition, sinon la légende finit par décrire une couleur que la grille n'utilise plus.
 */
export function OriginDot({ source }: { source: string }) {
    const imported = source !== 'manual';
    return (
        <span
            title={imported ? `Importée — ${source}` : 'Saisie à la main'}
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${imported ? DOT_IMPORTED : DOT_MANUAL}`}
        />
    );
}

export function OriginLegend() {
    return (
        <div className="flex items-center gap-4 flex-wrap px-4 py-2.5 bg-surface-soft border-t border-border">
            <span className="flex items-center gap-2 shrink-0">
                <span className={`w-1.5 h-1.5 rounded-full ${DOT_IMPORTED}`} />
                <span className="text-[11.5px] text-content-muted">Importée d'une plateforme</span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
                <span className={`w-1.5 h-1.5 rounded-full ${DOT_MANUAL}`} />
                <span className="text-[11.5px] text-content-muted">Saisie à la main</span>
            </span>
        </div>
    );
}
